import { useEffect, useState } from 'react';
import {
	getActiveLocations,
	ImportTypeLocation,
	ImportTypeLocationSchema,
	update,
} from '@/api/admin/location';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { ComboboxSelect } from '@/components/ui/custom/combobox-select';

export function UpdateLocation({ location }: { location: ImportTypeLocation }) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { data: activeLocations, isLoading: isLoadingActiveLocations } = useQuery({
		queryKey: [`active-locations`],
		queryFn: () => getActiveLocations(),
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
	});

	const { mutate } = useMutation({
		mutationFn: (updatedLocation: ImportTypeLocation) => update(updatedLocation),
		onSuccess: () => {
			toast.success(`Success! Location has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: [`locations-${location.importTypeId}`],
			});
		},
		onError: () => {
			toast.error(`Error! Location could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<ImportTypeLocation>({
		resolver: zodResolver(ImportTypeLocationSchema),
		defaultValues: location,
		mode: 'all',
	});

	const { reset } = form;
	useEffect(() => {
		reset(location);
	}, [location, reset]);

	const onSubmit: SubmitHandler<ImportTypeLocation> = (
		values: ImportTypeLocation,
	) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update Location</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Update Location</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-2">
								<FormField
									control={form.control}
									name="importTypeId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Import Type ID</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="compLocId"
									render={({ field }) => (
										<ComboboxSelect
											field={field}
											options={activeLocations}
											valueKey='compLocId'
											displayKey='brandName'
											isLoading={isLoadingActiveLocations}
											label="Company Location"
											placeholder="Select company location..."
											searchPlaceholder="Search company locations..."
											required={true}
											emptyMessage="No company location found."
											loadingMessage="Loading company locations..."
										/>
									)}
								/>								
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Updating...' : 'Update'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
